import React, { useEffect, useRef, useState } from 'react';

interface RevealProps {
  children: React.ReactNode;
  width?: 'fit-content' | '100%';
  delay?: number;
  threshold?: number;
  className?: string;
  direction?: 'up' | 'right';
}

export const Reveal: React.FC<RevealProps> = ({ 
  children, 
  width = '100%', 
  delay = 0,
  threshold = 0.15,
  className = '',
  direction = 'up'
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      {
        root: null,
        rootMargin: '0px 0px -50px 0px', // Trigger slightly before element is fully in view
        threshold: threshold,
      }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, [threshold]);

  const transformStyle = direction === 'up' 
    ? (isVisible ? 'translateY(0)' : 'translateY(40px)')
    : (isVisible ? 'translateX(0)' : 'translateX(-20px)');

  return (
    <div ref={ref} style={{ width }} className={`relative overflow-hidden ${className}`}>
      <div
        className={className.includes('h-full') ? 'h-full' : ''}
        style={{
          transform: transformStyle,
          opacity: isVisible ? 1 : 0,
          // Using a custom cubic-bezier for a "luxury" feel (starts fast, lands soft)
          transition: `all 1.1s cubic-bezier(0.16, 1, 0.3, 1) ${delay}s`,
          willChange: 'transform, opacity'
        }}
      >
        {children}
      </div>
    </div>
  );
};
